import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { auth, firestore, googleProvider, storage } from './firebase/config';
// Apple Sign In temporarily disabled
// import { appleProvider } from './firebase/config';
import { createUserWithEmailAndPassword, signInWithPopup, updateProfile, GoogleAuthProvider } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from './contexts/AuthContext';

const SignUp = () => {
  const locationHook = useLocation();
  const navigate = useNavigate();
  
  // Basic auth info
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [accountType, setAccountType] = useState('user'); // 'user' or 'business_user'
  
  // Profile info (step 1)
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [aboutMe, setAboutMe] = useState('');
  const [education, setEducation] = useState('');
  const [languages, setLanguages] = useState('');
  const [workLocation, setWorkLocation] = useState('');
  
  // Experience info (step 2 - for regular users)
  const [jobTitle, setJobTitle] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [workPeriod, setWorkPeriod] = useState('');
  const [skills, setSkills] = useState([]);
  
  // Job preferences (step 3 - for regular users)
  const [desiredJobs, setDesiredJobs] = useState([]);
  const [expectedRate, setExpectedRate] = useState('');
  const [availability, setAvailability] = useState({
    immediate: false,
    casual: false,
    notAvailable: false
  });
  const [startDate, setStartDate] = useState('');
  
  // Business info (for business users)
  const [businessInfo, setBusinessInfo] = useState({
    name: '',
    address: '',
    phone: '',
    website: '',
    industry: ''
  });
  
  // UI state
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const { setUserRole } = useAuth();

  // Set account type from location state if available
  useEffect(() => {
    if (locationHook.state?.preselectedRole) {
      setAccountType(locationHook.state.preselectedRole);
    }
  }, [locationHook.state]);

  // Handle account creation (step 1)
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    
    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update profile display name
      await updateProfile(userCredential.user, {
        displayName: name
      });
      
      // Store basic user data in Firestore
      await setDoc(doc(firestore, 'users', userCredential.user.uid), {
        firstName: name.split(' ')[0] || '',
        lastName: name.split(' ').slice(1).join(' ') || '',
        email: email,
        role: accountType,
        createdAt: new Date().toISOString()
      });
      
      console.log('User account created:', userCredential.user);
      setAccountCreated(true);
      setUserRole(accountType);
      // Proceed to next step
      setCurrentStep(2);
    } catch (error) {
      console.error('Error signing up:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle profile information submission (step 2)
  const handleProfileSubmit = async () => {
    if (!accountCreated) return;
    
    setLoading(true);
    try {
      // Create or update user profile in Firestore
      const profileData = {
        aboutMe,
        education,
        languages,
        workLocation,
        updatedAt: new Date().toISOString()
      };
      
      // Upload profile image if provided
      if (profileImage) {
        const storageRef = ref(storage, `userProfilePhoto/${auth.currentUser.uid}`);
        await uploadBytes(storageRef, profileImage);
        const downloadUrl = await getDownloadURL(storageRef);
        profileData.profileImagePath = `userProfilePhoto/${auth.currentUser.uid}`;
        profileData.profileImageUrl = downloadUrl;
      }
      
      await setDoc(doc(firestore, 'userProfiles', auth.currentUser.uid), profileData, { merge: true });
      
      // Move to next step
      setCurrentStep(3);
    } catch (error) {
      console.error('Error saving profile:', error);
      setError('Failed to save profile: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle experience submission (step 3 for regular users)
  const handleExperienceSubmit = async () => {
    if (!accountCreated || accountType !== 'user') return;
    
    setLoading(true);
    try {
      // Add work experience
      if (jobTitle && businessName) {
        await setDoc(doc(firestore, 'userProfiles', auth.currentUser.uid), {
          experience: [{
            jobTitle,
            businessName,
            description: jobDescription,
            period: workPeriod,
            skills: skills
          }],
          skills: skills,
          updatedAt: new Date().toISOString()
        }, { merge: true });
      }
      
      // Move to final step
      setCurrentStep(4);
    } catch (error) {
      console.error('Error saving experience:', error);
      setError('Failed to save experience: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle job preferences submission (final step for regular users)
  const handleJobPreferencesSubmit = async () => {
    if (!accountCreated || accountType !== 'user') return;
    
    setLoading(true);
    try {
      // Save job preferences
      await setDoc(doc(firestore, 'userProfiles', auth.currentUser.uid), {
        jobPreferences: {
          desiredJobs,
          expectedRate,
          availability: Object.keys(availability).filter(key => availability[key]),
          startDate
        },
        isProfileComplete: true,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      
      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving job preferences:', error);
      setError('Failed to save job preferences: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle business info submission (for business users)
  const handleBusinessInfoSubmit = async () => {
    if (!accountCreated || accountType !== 'business_user') return;
    
    setLoading(true);
    try {
      // Save business information
      await setDoc(doc(firestore, 'userProfiles', auth.currentUser.uid), {
        businessName: businessInfo.name,
        businessAddress: businessInfo.address,
        businessPhone: businessInfo.phone,
        businessWebsite: businessInfo.website,
        businessIndustry: businessInfo.industry,
        isProfileComplete: true,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      
      // Navigate to business dashboard
      navigate('/business-dashboard');
    } catch (error) {
      console.error('Error saving business info:', error);
      setError('Failed to save business information: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Move to next step
  const handleNextStep = () => {
    setCurrentStep(currentStep + 1);
  };
  
  // Go back to previous step
  const handlePrevStep = () => {
    setCurrentStep(currentStep - 1);
  };
  
  // Handle file selection for profile image
  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setProfileImage(e.target.files[0]);
      setProfileImageUrl(URL.createObjectURL(e.target.files[0]));
    }
  };
  
  // Add or remove a skill
  const handleSkillToggle = (skill) => {
    if (skills.includes(skill)) {
      setSkills(skills.filter(s => s !== skill));
    } else {
      setSkills([...skills, skill]);
    }
  };
  
  // Add or remove a job type
  const handleJobToggle = (job) => {
    if (desiredJobs.includes(job)) {
      setDesiredJobs(desiredJobs.filter(j => j !== job));
    } else {
      setDesiredJobs([...desiredJobs, job]);
    }
  };
  
  // Handle form submission based on current step
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // 如果是第一步，必须完成账户创建才能进入后续步骤
    if (currentStep === 1) {
      handleCreateAccount(e);
    } else if (accountCreated) {
      // 确保用户已创建账户后才能继续后续步骤
      switch(currentStep) {
        case 2:
          handleProfileSubmit();
          break;
        case 3:
          if (accountType === 'user') {
            handleExperienceSubmit();
          } else {
            handleBusinessInfoSubmit();
          }
          break;
        case 4:
          if (accountType === 'user') {
            handleJobPreferencesSubmit();
          }
          break;
        default:
          break;
      }
    } else {
      // 如果尚未创建账户，不允许进入后续步骤
      setCurrentStep(1);
      setError('请先完成账户创建');
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;
      
      // Store additional user data in Firestore
      await setDoc(doc(firestore, 'users', user.uid), {
        firstName: user.displayName?.split(' ')[0] || 'Anonymous',
        lastName: user.displayName?.split(' ').slice(1).join(' ') || 'User',
        email: user.email,
        role: accountType,
        createdAt: new Date().toISOString()
      });
      
      console.log('Google sign up successful:', user);
      setUserRole(accountType);
      
      // Redirect based on account type
      if (accountType === 'business_user') {
        navigate('/business-dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error signing up with Google:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Apple Sign In temporarily disabled
  // const handleAppleSignUp = async () => {
  //   setError('');
  //   setLoading(true);
  //   
  //   try {
  //     const result = await signInWithPopup(auth, appleProvider);
  //     
  //     // Store additional user data in Firestore
  //     await setDoc(doc(firestore, 'users', result.user.uid), {
  //       // Apple might not always provide displayName in the correct format
  //       firstName: result.user.displayName?.split(' ')[0] || 'Apple',
  //       lastName: result.user.displayName?.split(' ').slice(1).join(' ') || 'User',
  //       email: result.user.email,
  //       role: accountType,
  //       createdAt: new Date()
  //     });
      
  //     console.log('Apple sign up successful:', result.user);
  //     // Navigate to the dashboard route which will handle redirection based on role
  //     navigate('/dashboard');
  //   } catch (error) {
  //     console.error('Error signing up with Apple:', error);
  //     setError(error.message);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleClose = () => {
    navigate('/');
  };

  return (
    <div className="relative flex items-center justify-center min-h-screen w-full overflow-hidden bg-gradient-to-br from-orange-50 to-orange-100">
      {/* Background pattern */}
      <div className="absolute inset-0 grid grid-cols-4 grid-rows-3 gap-4 z-0">
        {Array(12).fill().map((_, i) => (
          <div key={i} className="rounded-lg bg-gradient-to-br from-orange-200 to-orange-100 opacity-50"></div>
        ))}
      </div>
      
      {/* Main container */}
      <div className="relative z-10 flex flex-row w-full max-w-5xl bg-white rounded-lg shadow-xl overflow-hidden">
        
        {/* Form side with step indicator */}
        <div className="w-full md:w-1/2 flex flex-row">
          
          {/* Step indicator - Left side */}
          <div className="flex flex-col justify-center items-center p-4 pt-8 pl-6">
            <div className="flex flex-col items-center space-y-4">
              {/* Step 1 */}
              <div className="flex flex-col items-center">
                <div className={`rounded-full w-8 h-8 flex items-center justify-center text-sm font-medium ${currentStep >= 1 ? 'bg-[#FF8C00] text-white' : 'bg-gray-200 text-gray-500'}`}>
                  1
                </div>
                {/* Line to Step 2 */}
                <div className={`h-6 w-0.5 ${currentStep >= 2 ? 'bg-[#FF8C00]' : 'bg-gray-200'}`}></div>
              </div>
              
              {/* Step 2 */}
              <div className="flex flex-col items-center">
                <div className={`rounded-full w-8 h-8 flex items-center justify-center text-sm font-medium ${currentStep >= 2 ? 'bg-[#FF8C00] text-white' : 'bg-gray-200 text-gray-500'}`}>
                  2
                </div>
                {/* Line to Step 3 */}
                <div className={`h-6 w-0.5 ${currentStep >= 3 ? 'bg-[#FF8C00]' : 'bg-gray-200'}`}></div>
              </div>
              
              {/* Step 3 */}
              <div className="flex flex-col items-center">
                <div className={`rounded-full w-8 h-8 flex items-center justify-center text-sm font-medium ${currentStep >= 3 ? 'bg-[#FF8C00] text-white' : 'bg-gray-200 text-gray-500'}`}>
                  3
                </div>
                {accountType === 'user' && (
                  <div className={`h-6 w-0.5 ${currentStep >= 4 ? 'bg-[#FF8C00]' : 'bg-gray-200'}`}></div>
                )}
              </div>
              
              {/* Step 4 (only for regular users) */}
              {accountType === 'user' && (
                <div className="flex flex-col items-center">
                  <div className={`rounded-full w-8 h-8 flex items-center justify-center text-sm font-medium ${currentStep >= 4 ? 'bg-[#FF8C00] text-white' : 'bg-gray-200 text-gray-500'}`}>
                    4
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Main form content */}
          <div className="flex-1 p-8 pt-12">
            <div className="flex items-center mb-6">
              <img 
                src="/asset/Logo/hireeLogo.png" 
                alt="Hiree Logo" 
                className="h-10 w-auto"
              />
              <span className="ml-3 text-xl text-gray-600">
                {currentStep === 1 && 'Create your account'}
                {currentStep === 2 && 'Tell us more about you'}
                {currentStep === 3 && accountType === 'user' && 'Tell us about your experience'}
                {currentStep === 3 && accountType === 'business_user' && 'Business information'}
                {currentStep === 4 && 'What job are you looking for'}
              </span>
            </div>
            
            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Step 1: Account Creation */}
              {currentStep === 1 && (
            <>
              {/* Account Type Selection */}
              <div className="text-center space-y-4">
                <Link 
                  to="/signup/regular" 
                  className="block w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  Sign up as Job Seeker
                </Link>
                <Link 
                  to="/signup/business" 
                  className="block w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700"
                >
                  Sign up as Business Owner
                </Link>
              </div>
              
              {/* Basic account information fields */}
              <div className="space-y-2">
                <label htmlFor="name" className="block text-left text-gray-700">
                  Full Name<span className="text-orange-500">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="email" className="block text-left text-gray-700">
                  Email Address<span className="text-orange-500">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="password" className="block text-left text-gray-700">
                  Password<span className="text-orange-500">*</span>
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
                <p className="text-xs text-gray-500">Password must be at least 6 characters</p>
              </div>
              
              <div className="space-y-2">
                <label htmlFor="confirm-password" className="block text-left text-gray-700">
                  Confirm Password<span className="text-orange-500">*</span>
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#FF8C00] text-white rounded-md hover:bg-[#FF8C00]/90 transition-colors disabled:opacity-70"
              >
                {loading ? 'Creating Account...' : 'Continue'}
              </button>
            
              <div className="relative flex items-center justify-center my-4">
                <div className="border-t border-gray-300 w-full"></div>
                <span className="bg-white px-3 text-sm text-gray-500 absolute">or</span>
              </div>
              
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="w-full py-3 border border-gray-300 rounded-md flex items-center justify-center space-x-2 hover:bg-gray-50 transition-colors disabled:opacity-70"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <span>Continue with Google</span>
              </button>
              
              {/* Apple Sign In button - temporarily disabled
              <button
                type="button"
                onClick={handleAppleSignUp}
                disabled={loading}
                className="w-full py-3 border border-gray-300 rounded-md flex items-center justify-center space-x-2 hover:bg-gray-50 transition-colors disabled:opacity-70"
              >
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" fill="currentColor">
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                </svg>
                <span>Continue with Apple</span>
              </button>
              */}
              
              <div className="text-center mt-6">
                <p className="text-sm text-gray-600">
                  Already have an account?{' '}
                  <Link to="/login" className="text-[#FF8C00] hover:underline">
                    Log in
                  </Link>
                </p>
              </div>
            </>
          )}
          
          {/* Step 2: Profile Information */}
          {currentStep === 2 && (
            <>
              <div className="space-y-6">
                {/* Profile Picture */}
                <div className="flex flex-col items-center space-y-3">
                  <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-2 border-[#FF8C00]">
                    {profileImageUrl ? (
                      <img src={profileImageUrl} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    )}
                  </div>
                  <label htmlFor="profile-image" className="cursor-pointer text-[#FF8C00] hover:underline text-sm">
                    Upload Photo
                    <input
                      id="profile-image"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                </div>
                
                {/* About Me */}
                <div className="space-y-2">
                  <label htmlFor="about-me" className="block text-left text-gray-700">
                    About Me
                  </label>
                  <textarea
                    id="about-me"
                    value={aboutMe}
                    onChange={(e) => setAboutMe(e.target.value)}
                    placeholder="Tell us a bit about yourself..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 min-h-[100px]"
                  />
                </div>
                
                {/* Education */}
                <div className="space-y-2">
                  <label htmlFor="education" className="block text-left text-gray-700">
                    Education
                  </label>
                  <select
                    id="education"
                    value={education}
                    onChange={(e) => setEducation(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                  >
                    <option value="">Select your highest education level</option>
                    <option value="High School">High School</option>
                    <option value="Some College">Some College</option>
                    <option value="Associate's Degree">Associate's Degree</option>
                    <option value="Bachelor's Degree">Bachelor's Degree</option>
                    <option value="Master's Degree">Master's Degree</option>
                    <option value="Doctorate">Doctorate</option>
                    <option value="Vocational Training">Vocational Training</option>
                    <option value="Culinary School">Culinary School</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                {/* Languages */}
                <div className="space-y-2">
                  <label htmlFor="languages" className="block text-left text-gray-700">
                    Languages
                  </label>
                  <div className="flex flex-wrap gap-2 border border-gray-300 rounded-md p-2">
                    {[
                      { id: 'english', name: 'English' },
                      { id: 'spanish', name: 'Spanish' },
                      { id: 'french', name: 'French' },
                      { id: 'chinese', name: 'Chinese' },
                      { id: 'japanese', name: 'Japanese' },
                      { id: 'korean', name: 'Korean' },
                      { id: 'italian', name: 'Italian' },
                      { id: 'german', name: 'German' },
                    ].map((lang) => {
                      const isSelected = languages.includes(lang.name);
                      return (
                        <button
                          key={lang.id}
                          type="button"
                          onClick={() => {
                            if (isSelected) {
                              setLanguages(languages.split(',').filter(l => l.trim() !== lang.name).join(','));
                            } else {
                              const newLanguages = languages ? `${languages}, ${lang.name}` : lang.name;
                              setLanguages(newLanguages);
                            }
                          }}
                          className={`px-3 py-1 text-sm rounded-full ${isSelected ? 'bg-orange-100 text-orange-700 border border-orange-300' : 'bg-gray-100 text-gray-700 border border-gray-200'}`}
                        >
                          {lang.name}
                        </button>
                      );
                    })}
                  </div>
                  <input
                    id="languages"
                    type="text"
                    value={languages}
                    onChange={(e) => setLanguages(e.target.value)}
                    placeholder="Or type other languages"
                    className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
                
                {/* Location */}
                <div className="space-y-2">
                  <label htmlFor="work-location" className="block text-left text-gray-700">
                    Location
                  </label>
                  <select
                    id="work-location"
                    value={workLocation}
                    onChange={(e) => setWorkLocation(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                  >
                    <option value="">Select your location</option>
                    <optgroup label="Main Cities">
                      <option value="New York, NY">New York, NY</option>
                      <option value="Los Angeles, CA">Los Angeles, CA</option>
                      <option value="Chicago, IL">Chicago, IL</option>
                      <option value="Houston, TX">Houston, TX</option>
                      <option value="Phoenix, AZ">Phoenix, AZ</option>
                      <option value="Philadelphia, PA">Philadelphia, PA</option>
                      <option value="San Antonio, TX">San Antonio, TX</option>
                      <option value="San Diego, CA">San Diego, CA</option>
                      <option value="Dallas, TX">Dallas, TX</option>
                      <option value="San Jose, CA">San Jose, CA</option>
                    </optgroup>
                    <optgroup label="Other">
                      <option value="other">Other (specify below)</option>
                    </optgroup>
                  </select>
                  {workLocation === 'other' && (
                    <input
                      type="text"
                      placeholder="Enter your location"
                      className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                      onChange={(e) => setWorkLocation(e.target.value)}
                    />
                  )}
                </div>
                
                {/* Navigation Buttons */}
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={handlePrevStep}
                    className="flex-1 py-3 border border-[#FF8C00] text-[#FF8C00] rounded-md hover:bg-orange-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleProfileSubmit}
                    disabled={loading}
                    className="flex-1 py-3 bg-[#FF8C00] text-white rounded-md hover:bg-[#FF8C00]/90 transition-colors disabled:opacity-70"
                  >
                    {loading ? 'Saving...' : 'Continue'}
                  </button>
                </div>
              </div>
            </>
          )}
          
          {/* Step 3: Experience (Regular Users) */}
          {currentStep === 3 && accountType === 'user' && (
            <>
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-700 mb-2">Work Experience</h3>
                <p className="text-sm text-gray-500 mb-4">Tell us about your most recent relevant work experience</p>
                
                {/* Job Title */}
                <div className="space-y-2">
                  <label htmlFor="job-title" className="block text-left text-gray-700">
                    Job Title
                  </label>
                  <input
                    id="job-title"
                    type="text"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    placeholder="e.g. Waiter, Chef, Bartender"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
                
                {/* Business Name */}
                <div className="space-y-2">
                  <label htmlFor="business-name" className="block text-left text-gray-700">
                    Business Name
                  </label>
                  <input
                    id="business-name"
                    type="text"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="e.g. The Grand Restaurant"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
                
                {/* Work Period */}
                <div className="space-y-2">
                  <label className="block text-left text-gray-700">
                    Work Period
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="work-start" className="block text-left text-gray-500 text-sm mb-1">
                        Start Date
                      </label>
                      <input
                        id="work-start"
                        type="month"
                        value={workPeriod.split(' - ')[0] || ''}
                        onChange={(e) => {
                          const endPart = workPeriod.split(' - ')[1] || 'Present';
                          setWorkPeriod(`${e.target.value} - ${endPart}`);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                      />
                    </div>
                    <div>
                      <label htmlFor="work-end" className="block text-left text-gray-500 text-sm mb-1">
                        End Date
                      </label>
                      <div className="relative">
                        <input
                          id="work-end"
                          type="month"
                          value={workPeriod.split(' - ')[1] === 'Present' ? '' : (workPeriod.split(' - ')[1] || '')}
                          onChange={(e) => {
                            const startPart = workPeriod.split(' - ')[0] || '';
                            setWorkPeriod(`${startPart} - ${e.target.value}`);
                          }}
                          disabled={workPeriod.split(' - ')[1] === 'Present'}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                        />
                        <div className="mt-1">
                          <input
                            type="checkbox"
                            id="current-job"
                            checked={workPeriod.split(' - ')[1] === 'Present'}
                            onChange={(e) => {
                              const startPart = workPeriod.split(' - ')[0] || '';
                              if (e.target.checked) {
                                setWorkPeriod(`${startPart} - Present`);
                              } else {
                                setWorkPeriod(`${startPart} - `);
                              }
                            }}
                            className="mr-2"
                          />
                          <label htmlFor="current-job" className="text-sm text-gray-600">Current job</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Job Description */}
                <div className="space-y-2">
                  <label htmlFor="job-description" className="block text-left text-gray-700">
                    Description
                  </label>
                  <textarea
                    id="job-description"
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                    placeholder="Briefly describe your responsibilities and achievements..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 min-h-[80px]"
                  />
                </div>
                
                {/* Skills */}
                <div className="space-y-2">
                  <label className="block text-left text-gray-700">
                    Skills
                  </label>
                  <div className="flex flex-wrap gap-2 border border-gray-300 rounded-md p-2 max-h-60 overflow-y-auto">
                    {[
                      // 服务技能
                      { id: 'customer-service', name: 'Customer Service' },
                      { id: 'team-work', name: 'Team Work' },
                      { id: 'multitasking', name: 'Multitasking' },
                      { id: 'conflict-resolution', name: 'Conflict Resolution' },
                      { id: 'communication', name: 'Communication' },
                      { id: 'time-management', name: 'Time Management' },
                      
                      // 餐饮技能
                      { id: 'food-preparation', name: 'Food Preparation' },
                      { id: 'menu-knowledge', name: 'Menu Knowledge' },
                      { id: 'food-safety', name: 'Food Safety' },
                      { id: 'cooking', name: 'Cooking' },
                      { id: 'food-plating', name: 'Food Plating' },
                      { id: 'food-service', name: 'Food Service' },
                      { id: 'dishwashing', name: 'Dishwashing' },
                      
                      // 饮品技能
                      { id: 'bartending', name: 'Bartending' },
                      { id: 'cocktail-mixing', name: 'Cocktail Mixing' },
                      { id: 'wine-knowledge', name: 'Wine Knowledge' },
                      { id: 'barista-skills', name: 'Barista Skills' },
                      
                      // 管理技能
                      { id: 'team-management', name: 'Team Management' },
                      { id: 'scheduling', name: 'Scheduling' },
                      { id: 'inventory-management', name: 'Inventory Management' },
                      { id: 'staff-training', name: 'Staff Training' },
                      
                      // 技术技能
                      { id: 'pos-systems', name: 'Point of Sale Systems' },
                      { id: 'cash-handling', name: 'Cash Handling' },
                      { id: 'reservation-systems', name: 'Reservation Systems' },
                      { id: 'order-taking', name: 'Order Taking' },
                      
                    ].map((skill) => {
                      const isSelected = skills.includes(skill.name);
                      return (
                        <button
                          key={skill.id}
                          type="button"
                          onClick={() => handleSkillToggle(skill.name)}
                          className={`px-3 py-1 text-sm rounded-full transition-colors ${isSelected ? 'bg-orange-100 text-orange-700 border border-orange-300' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:border-gray-300'}`}
                        >
                          {skill.name}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex items-center mt-2">
                    <input
                      type="text"
                      placeholder="Add another skill"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.target.value.trim()) {
                          e.preventDefault();
                          handleSkillToggle(e.target.value.trim());
                          e.target.value = '';
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        const input = e.target.previousSibling;
                        if (input && input.value.trim()) {
                          handleSkillToggle(input.value.trim());
                          input.value = '';
                        }
                      }}
                      className="px-4 py-2 bg-[#FF8C00] text-white rounded-r-md hover:bg-[#FF8C00]/90"
                    >
                      Add
                    </button>
                  </div>
                </div>
                
                {/* Navigation Buttons */}
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={handlePrevStep}
                    className="flex-1 py-3 border border-[#FF8C00] text-[#FF8C00] rounded-md hover:bg-orange-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleExperienceSubmit}
                    disabled={loading}
                    className="flex-1 py-3 bg-[#FF8C00] text-white rounded-md hover:bg-[#FF8C00]/90 transition-colors disabled:opacity-70"
                  >
                    {loading ? 'Saving...' : 'Continue'}
                  </button>
                </div>
              </div>
            </>
          )}
          
          {/* Step 3: Business Info (Business Users) */}
          {currentStep === 3 && accountType === 'business_user' && (
            <>
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-700 mb-2">Business Information</h3>
                <p className="text-sm text-gray-500 mb-4">Tell us about your business</p>
                
                {/* Business Name */}
                <div className="space-y-2">
                  <label htmlFor="business-info-name" className="block text-left text-gray-700">
                    Business Name
                  </label>
                  <input
                    id="business-info-name"
                    type="text"
                    value={businessInfo.name}
                    onChange={(e) => setBusinessInfo({...businessInfo, name: e.target.value})}
                    placeholder="Your business name"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
                
                {/* Business Address */}
                <div className="space-y-2">
                  <label htmlFor="business-address" className="block text-left text-gray-700">
                    Business Address
                  </label>
                  <input
                    id="business-address"
                    type="text"
                    value={businessInfo.address}
                    onChange={(e) => setBusinessInfo({...businessInfo, address: e.target.value})}
                    placeholder="Full address"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
                
                {/* Business Phone */}
                <div className="space-y-2">
                  <label htmlFor="business-phone" className="block text-left text-gray-700">
                    Phone Number
                  </label>
                  <input
                    id="business-phone"
                    type="tel"
                    value={businessInfo.phone}
                    onChange={(e) => setBusinessInfo({...businessInfo, phone: e.target.value})}
                    placeholder="e.g. (123) 456-7890"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
                
                {/* Business Website */}
                <div className="space-y-2">
                  <label htmlFor="business-website" className="block text-left text-gray-700">
                    Website (optional)
                  </label>
                  <input
                    id="business-website"
                    type="url"
                    value={businessInfo.website}
                    onChange={(e) => setBusinessInfo({...businessInfo, website: e.target.value})}
                    placeholder="e.g. https://yourrestaurant.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
                
                {/* Business Industry */}
                <div className="space-y-2">
                  <label htmlFor="business-industry" className="block text-left text-gray-700">
                    Industry
                  </label>
                  <select
                    id="business-industry"
                    value={businessInfo.industry}
                    onChange={(e) => setBusinessInfo({...businessInfo, industry: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                  >
                    <option value="">Select Industry</option>
                    <option value="Restaurant">Restaurant</option>
                    <option value="Cafe">Cafe</option>
                    <option value="Bar">Bar & Nightclub</option>
                    <option value="Hotel">Hotel & Accommodation</option>
                    <option value="Catering">Catering</option>
                    <option value="Other">Other Hospitality</option>
                  </select>
                </div>
                
                {/* Navigation Buttons */}
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={handlePrevStep}
                    className="flex-1 py-3 border border-[#FF8C00] text-[#FF8C00] rounded-md hover:bg-orange-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleBusinessInfoSubmit}
                    disabled={loading}
                    className="flex-1 py-3 bg-[#FF8C00] text-white rounded-md hover:bg-[#FF8C00]/90 transition-colors disabled:opacity-70"
                  >
                    {loading ? 'Saving...' : 'Complete'}
                  </button>
                </div>
              </div>
            </>
          )}
          
          {/* Step 4: Job Preferences (Regular Users) */}
          {currentStep === 4 && accountType === 'user' && (
            <>
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-700 mb-2">Job Preferences</h3>
                <p className="text-sm text-gray-500 mb-4">Tell us what you're looking for</p>
                
                {/* Desired Job Types */}
                <div className="space-y-2">
                  <label className="block text-left text-gray-700">
                    Desired Job Types
                  </label>
                  <div className="flex flex-wrap gap-2 border border-gray-300 rounded-md p-3">
                    {[
                      // 餐厅前台
                      { id: 'waiter', name: 'Waiter/Waitress', category: 'Front of House' },
                      { id: 'host', name: 'Host/Hostess', category: 'Front of House' },
                      { id: 'server', name: 'Server', category: 'Front of House' },
                      { id: 'busser', name: 'Busser', category: 'Front of House' },
                      { id: 'food-runner', name: 'Food Runner', category: 'Front of House' },
                      
                      // 餐厅后台
                      { id: 'chef', name: 'Chef', category: 'Back of House' },
                      { id: 'line-cook', name: 'Line Cook', category: 'Back of House' },
                      { id: 'prep-cook', name: 'Prep Cook', category: 'Back of House' },
                      { id: 'dishwasher', name: 'Dishwasher', category: 'Back of House' },
                      { id: 'kitchen-staff', name: 'Kitchen Staff', category: 'Back of House' },
                      
                      // 酒吧相关
                      { id: 'bartender', name: 'Bartender', category: 'Bar' },
                      { id: 'barback', name: 'Barback', category: 'Bar' },
                      { id: 'bar-manager', name: 'Bar Manager', category: 'Bar' },
                      { id: 'barista', name: 'Barista', category: 'Bar' },
                      
                      // 管理职位
                      { id: 'manager', name: 'Manager', category: 'Management' },
                      { id: 'assistant-manager', name: 'Assistant Manager', category: 'Management' },
                      { id: 'shift-leader', name: 'Shift Leader', category: 'Management' },
                      { id: 'supervisor', name: 'Supervisor', category: 'Management' },
                    ].map((job) => {
                      const isSelected = desiredJobs.includes(job.name);
                      return (
                        <button
                          key={job.id}
                          type="button"
                          onClick={() => handleJobToggle(job.name)}
                          className={`px-3 py-1 text-sm rounded-full transition-colors ${isSelected ? 'bg-orange-100 text-orange-700 border border-orange-300' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:border-gray-300'}`}
                        >
                          {job.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Expected Rate */}
                <div className="space-y-2">
                  <label htmlFor="expected-rate" className="block text-left text-gray-700">
                    Expected Hourly Rate
                  </label>
                  <input
                    id="expected-rate"
                    type="text"
                    value={expectedRate}
                    onChange={(e) => setExpectedRate(e.target.value)}
                    placeholder="e.g. $15-20 per hour"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
                
                {/* Availability */}
                <div className="space-y-2">
                  <label className="block text-left text-gray-700">
                    Availability
                  </label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="availability-immediate"
                        checked={availability.immediate}
                        onChange={() => setAvailability({...availability, immediate: !availability.immediate})}
                        className="h-4 w-4 text-[#FF8C00] focus:ring-[#FF8C00]"
                      />
                      <label htmlFor="availability-immediate" className="text-sm text-gray-700">
                        Immediate Start
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="availability-casual"
                        checked={availability.casual}
                        onChange={() => setAvailability({...availability, casual: !availability.casual})}
                        className="h-4 w-4 text-[#FF8C00] focus:ring-[#FF8C00]"
                      />
                      <label htmlFor="availability-casual" className="text-sm text-gray-700">
                        Casual / Part-time
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="availability-not-available"
                        checked={availability.notAvailable}
                        onChange={() => setAvailability({...availability, notAvailable: !availability.notAvailable})}
                        className="h-4 w-4 text-[#FF8C00] focus:ring-[#FF8C00]"
                      />
                      <label htmlFor="availability-not-available" className="text-sm text-gray-700">
                        Not immediately available
                      </label>
                    </div>
                  </div>
                </div>
                
                {/* Start Date */}
                <div className="space-y-2">
                  <label htmlFor="start-date" className="block text-left text-gray-700">
                    Start Date (if not immediate)
                  </label>
                  <input
                    id="start-date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
                
                {/* Navigation Buttons */}
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={handlePrevStep}
                    className="flex-1 py-3 border border-[#FF8C00] text-[#FF8C00] rounded-md hover:bg-orange-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleJobPreferencesSubmit}
                    disabled={loading}
                    className="flex-1 py-3 bg-[#FF8C00] text-white rounded-md hover:bg-[#FF8C00]/90 transition-colors disabled:opacity-70"
                  >
                    {loading ? 'Completing...' : 'Complete Profile'}
                  </button>
                </div>
              </div>
            </>
          )}
            </form>
          </div>
        </div>
        
        {/* Video/Image side */}
        <div className="hidden md:block w-1/2 relative">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-20 bg-white rounded-full p-2 shadow-md hover:bg-gray-100"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-orange-700/20 z-10"></div>
          <video
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
          >
            <source src="/asset/SignInPage/video1.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="absolute bottom-8 left-8 z-20 text-white">
            <h2 className="text-2xl font-bold mb-2">Join Hiree</h2>
            <p className="text-sm opacity-90">Connect with restaurants and find your next opportunity</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
